import { v4 } from 'uuid';
import { AppDelivery, CartItem, CatalogueItem, Collection, PriceType } from 'src/types';
import { PRODUCT_CATEGORY_NAME_MAP, VIRTUAL_DEVICES } from 'src/constants';
/**
 * Calculates the total quantity of the cart.
 * @param {CartItem[]} cart - The cart.
 * @returns {number} The total quantity of the cart.
 */
export function calculateTotalCartQuantity(cart: CartItem[]): number {
  return cart.reduce((total, item) => {
    if (!item.isTenderType) {
      total += item.quantity
    }
    return total
  }, 0);
}

/**
 * Calculates the total price of items in the cart.
 *
 * @param {CartItem[]} cart - The array of cart items.
 * @returns {number} The total price of items in the cart.
 */
export function calculateCartTotal(cart: CartItem[], includeRentals?: boolean): number {
  return cart.reduce((total, item) => {
    if (item.priceType === PriceType.ONCE_OFF || includeRentals) {
      return total + (item.price || 0) * item.quantity;
    }
    return total;
  }, 0);
}

/**
 * Normalizes the quantity of a product in the cart by subtracting the quantities of the product in deliveries and collections.
 *
 * @param {string} productId - The ID of the product.
 * @param {number} cartQuantity - The quantity of the product in the cart.
 * @param {AppDelivery[]} deliveries - The deliveries.
 * @param {Collection[]} collections - The collections.
 * @returns {number} - Returns the normalized quantity of the product in the cart.
 */
export function normalizeQuantity(
  productId: string,
  cartQuantity: number,
  deliveries: AppDelivery[],
  collections: Collection[],
): number {
  const deliveryItems = deliveries.filter(
    (item) => item.productId === productId && item.addressId !== 'default',
  );
  const collectionItems = collections.filter(
    (item) => item.productId === productId && item.addressId !== 'default',
  );

  const deliveriesQuantity = deliveryItems.reduce((total, item) => {
    return total + item.quantity;
  }, 0);

  const collectionsQuantity = collectionItems.reduce((total, item) => {
    return total + item.quantity;
  }, 0);

  return cartQuantity - (deliveriesQuantity + collectionsQuantity);
}

/**
 * Calculates the total quantity of tender type products in the cart.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {CatalogueItem[]} products - The catalogue items.
 * @returns {number} - Returns the total quantity of tender type products in the cart.
 */
export function getTenderTypeCount(cart: CartItem[], products: CatalogueItem[]) {

  const tenderTypeCount = products
    .filter((product) => product.device.productCategoryName && !product.device.isProductCategory)
    .reduce((accum, curr) => {
      const cartItem = cart.find((item) => item.productId === curr.device.id);

      return accum + (cartItem?.quantity || 0);
    }, 0);

  return tenderTypeCount;

}

/**
 * Calculates the total quantity of tender type products in the cart.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {CatalogueItem[]} products - The catalogue items.
 * @returns {number} - Returns the total quantity of tender type products in the cart.
 */
export function getTenderTypes(products: CatalogueItem[]): CartItem[] {
  const tenderTypes = products.filter((product) => product.device.productCategoryName && !product.device.isProductCategory);

  return tenderTypes.map((product) => {
    return {
      productId: product.device.id,
      acquiringChannel: product.device.acquiringChannel,
      title: product.device.title,
      quantity: 0,
      tagline: product.device.tagline,
      price: product.price.price,
      priceType: product.price.priceType,
      deliveryOptions: product.device.deliveryOptions,
      image: product.device.imageBase64 || '',
      productCategoryName: product.device.productCategoryName,
      isTenderType: true,
      id: v4()
    };
  }
  );
}

/**
 * Checks if a product is a virtual device.
 * @param {string} title - The title of the product.
 * @returns {boolean} - Returns true if the product is a virtual device, false otherwise.
 */
export function isVirtualDevice(title: string): boolean {
  return VIRTUAL_DEVICES.includes(title);
}

/**
 * Checks if an item is in the cart by its product ID.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {string} id - The product ID to check.
 * @returns {boolean} - Returns true if the item is in the cart, false otherwise.
 */
export function isItemInCart(cart: CartItem[], id: string): boolean {
  return cart.some(item => item.productId === id);
}

/**
 * Checks if an e-commerce item is in the cart.
 *
 * @param {CartItem[]} cart - The cart items.
 * @returns {boolean} - Returns true if an e-commerce item is in the cart, false otherwise.
 */
export function isECommerceItemInCart(cart: CartItem[]): boolean {
  return cart.some(item => Object.keys(PRODUCT_CATEGORY_NAME_MAP).includes(item.title));
}

/**
 * Checks if the cart contains a single item with the specified title.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {string} title - The title of the item to check.
 * @returns {boolean} - Returns true if the cart contains a single item with the specified title, false otherwise.
 */
export function isSingleItemInCart(cart: CartItem[], title: string): boolean {
  return cart.length === 1 && cart[0].title === title;
}

/**
 * Checks if all items in the cart are virtual devices.
 *
 * @param {CartItem[]} cart - The cart items.
 * @returns {boolean} - Returns true if all items in the cart are virtual devices, false otherwise.
 */
export function isVirtualOnlyCart(cart: CartItem[]): boolean {
  return cart.every(item => isVirtualDevice(item.title));
}

/**
 * Filters the cart items by price type.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {PriceType} priceType - The price type to filter by.
 * @returns {CartItem[]} - Returns an array of cart items that match the specified price type and are not tender types.
 */
export function filterCartItemsByPriceType(cart: CartItem[], priceType: PriceType): CartItem[] {
  return cart.filter(item => item.priceType === priceType && !item.isTenderType);
}

/**
 * Checks if an item is in the cart by its title.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {string} title - The title of the item to check.
 * @returns {boolean} - Returns true if the item is in the cart and its quantity is greater than 0, false otherwise.
 * @deprecated use isItemsInCart instead
 */
export function isItemInCartByTitle(cart: CartItem[], title: string): boolean {
  return cart.some(item => item.title === title && item.quantity > 0);
}

/**
 * Checks if an item is in the cart by its title.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {string} titles - The titles of the items to check.
 * @returns {boolean} - Returns true if the item is in the cart and its quantity is greater than 0, false otherwise.
 */
export function isItemsInCart(cart: CartItem[], titles: string[]): boolean {
  return titles.some(title => isItemInCartByTitle(cart, title));
}

/**
 * Checks if an item is in the cart by price type.
 *
 * @param {CartItem[]} cart - The cart items.
 * @param {string} productId - The product ID to check.
 * @param {PriceType} priceType - The price type to check.
 * @returns {CartItem | undefined} - Returns the cart item if it is in the cart, undefined otherwise.
 */
export function findItemInCartByPriceType(cart: CartItem[], productId: string, priceType: PriceType): CartItem | undefined {
  return cart.find((item) => item.productId === productId && item.priceType === priceType);
};

/**
 * Checks if the cart contains once-off items.
 *
 * @param {CartItem[]} cart - The cart items.
 * @returns {boolean} - Returns true if the cart contains once-off items, false otherwise.
 */
export function doesCartContainOnceOffItems(cart: CartItem[]): boolean {
  return cart.some(item => item.priceType === PriceType.ONCE_OFF);
}